import React from 'react';
import { css, cx } from 'styled-system/css';

import { DESIGN_IDS, DESIGN_ONLY_ATTRIBUTE, type DesignId } from '../config';

type Tag = 'div' | 'span';

interface DesignOnlyProps {
  /** The design(s) this content belongs to */
  design: DesignId | DesignId[];
  /** `span` inside text, `div` (default) elsewhere */
  as?: Tag;
  className?: string;
  children?: React.ReactNode;
}

const contents = css({ display: 'contents' });

/**
 * Content for some designs only. It is rendered for everyone (so pages stay static and switching
 * is instant) and hidden by CSS unless its design is active; hidden content is also skipped by
 * screen readers and never starts its in-view animations.
 * The wrapper is `display: contents`, so it doesn't affect layout.
 */
export function DesignOnly({ design, as = 'div', className, children }: DesignOnlyProps) {
  const ids = Array.isArray(design) ? design : [design];
  return React.createElement(
    as,
    { [DESIGN_ONLY_ATTRIBUTE]: ids.join(' '), className: cx(contents, className) },
    children,
  );
}

type Variants = Partial<Record<DesignId, React.ReactNode>>;

/** One piece of UI per design: `<DesignVariants storybook={<A />} nebula={<B />} />`. */
export function DesignVariants({ as, ...variants }: Variants & { as?: Tag }) {
  return (
    <>
      {DESIGN_IDS.filter((id) => variants[id] !== undefined).map((id) => (
        <DesignOnly key={id} design={id} as={as}>
          {variants[id]}
        </DesignOnly>
      ))}
    </>
  );
}

/** Copy that differs per design. Identical strings render once, without wrappers. */
export function DesignText(variants: Record<DesignId, React.ReactNode>) {
  const values = DESIGN_IDS.map((id) => variants[id]);
  if (values.every((value) => value === values[0])) return <>{values[0]}</>;
  return <DesignVariants as='span' {...variants} />;
}
